/** A stored value, read-only (SA-449, spec §8): a table as a compact table
 * under its column labels, every other type as its text — the Needs panel's
 * one renderer, so step 5's Dispatch request shows the sectors with no
 * module code. */
import { PlaceStations } from '@/components/records/PlaceStations'
import { Station } from '@/components/records/Station'
import { EITHER } from '@/components/records/placeText'
import type { Airport } from '@/types/airports'
import { isPlace, placeCodes, placeState, placeStationWords, OR } from '@/components/records/place'
import { isWindow } from '@/components/records/window'
import type { TaskColumnRead, TaskFieldRead } from '@/types/process'
import { isBlank } from './fieldState'
import { NEW_ROW, wasText } from './fieldStateText'
import { cellChange, formatCell, referenceColumns, rowAdded, rowsOf, visibleColumns } from './tableRows'
import { WindowValue } from './WindowValue'

type Shown = Pick<TaskFieldRead, 'type' | 'value' | 'label'> & { columns?: TaskColumnRead[] | null }

const NO_LOOKUP: Record<string, Airport> = {}

/** SA-466: the empty marker is muted everywhere, so it never reads as a value. */
const Dash = () => <span className="text-muted-foreground">—</span>

const LONG_CELL = 32

/** A string cell past 32 characters is cut, the full text in its `title`; numbers and dates never. */
function CellText({ value, type }: { value: unknown; type: string }) {
  const text = formatCell(value, type)
  if (typeof value === 'string' && text.length > LONG_CELL) {
    return <span className="inline-block max-w-[16rem] truncate align-bottom" title={text}>{text}</span>
  }
  return <>{text}</>
}

export function FieldValue({ field }: { field: Shown }) {
  const { value } = field
  if (value === null || value === undefined) return <Dash />
  if (field.type === 'table' && field.columns) {
    const declared = field.columns
    const marked = referenceColumns(declared).size > 0
    const rows = rowsOf(value)
    if (rows.length === 0) return <Dash />
    // A column nothing fills is left out; one whose reference is filled stays, so its "was" marks show.
    const columns = visibleColumns(declared).filter((c) => rows.some((r) => !isBlank(r[c.id]) || (c.reference ? !isBlank(r[c.reference]) : false)))
    if (columns.length === 0) return <Dash />   // every column blank: nothing to show, as an empty table
    return (
      <div className="max-w-full overflow-x-auto">
      <table aria-label={field.label} className="whitespace-nowrap text-xs">
        <thead>
          <tr className="text-left text-muted-foreground">
            {columns.map((c) => <th key={c.id} scope="col" className="pr-3 font-medium">{c.label}</th>)}
            {marked && <th scope="col" className="pr-3 font-medium"><span className="sr-only">Changes</span></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const added = rowAdded(row, declared)
            return (
              <tr key={i} data-row={added ? 'added' : undefined}>
                {columns.map((c) => {
                  const change = cellChange(row, c, declared)
                  return (
                    <td key={c.id} className="pr-3" data-change={change === 'changed' ? change : undefined}>
                      <CellText value={row[c.id]} type={c.type} />
                      {change === 'changed' && <> <s className="text-muted-foreground">{wasText(row[c.reference as string], c.type)}</s></>}
                    </td>
                  )
                })}
                {marked && <td className="pr-3 text-muted-foreground">{added ? NEW_ROW : ''}</td>}
              </tr>
            )
          })}
        </tbody>
      </table>
      </div>
    )
  }
  if (isWindow(value)) return <WindowValue value={value} />   // SA-1136: keyed on the VALUE, as a place is
  if (isPlace(value)) {
    // SA-1051 (spec §7.3): provider-free, from the stored words.
    const state = placeState(value)
    if (state === 'resolved') return <Station text={value.code ?? ''} airport={placeStationWords(value)} />
    if (state === 'choice') return <>{`${value.text}: ${placeCodes(value).join(OR)}?`}</>
    if (state === 'alternatives') return <>{EITHER} <PlaceStations value={value} airports={NO_LOOKUP} /></>
    if (state === 'unknown') return <span className="font-mono">{value.code}</span>
    return <>{value.text}</>
  }
  return <>{String(value)}</>
}
